async function getCsrf(): Promise<void> {
  await fetch('/sanctum/csrf-cookie', { credentials: 'include' })
}

function readXsrfToken(): string {
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith('XSRF-TOKEN='))
  if (!match) return ''
  return decodeURIComponent(match.slice('XSRF-TOKEN='.length))
}

export interface LoginPayload {
  email: string
  password: string
  remember?: boolean
}

export class ApiError extends Error {
  status: number
  errors?: Record<string, string[]>
  code?: string
  payload?: Record<string, unknown>

  constructor(
    status: number,
    message: string,
    errors?: Record<string, string[]>,
    code?: string,
    payload?: Record<string, unknown>,
  ) {
    super(message)
    this.status = status
    this.errors = errors
    this.code = code
    this.payload = payload
  }
}

export async function login(payload: LoginPayload): Promise<void> {
  await getCsrf()
  const xsrf = readXsrfToken()
  const r = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-XSRF-TOKEN': xsrf,
    },
    body: JSON.stringify(payload),
  })
  if (r.status === 204) return
  if (r.status === 422) {
    const j = (await r.json()) as { message?: string; errors?: Record<string, string[]> }
    throw new ApiError(422, j.message ?? 'Validation failed', j.errors)
  }
  throw new ApiError(r.status, `Login failed (${r.status})`)
}

export async function logout(): Promise<void> {
  const xsrf = readXsrfToken()
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-XSRF-TOKEN': xsrf,
    },
  })
}

export interface SelectCompanyResponse {
  current_company_id: string
}

export async function selectCompany(companyId: string): Promise<SelectCompanyResponse> {
  const xsrf = readXsrfToken()
  const r = await fetch('/api/auth/select-company', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-XSRF-TOKEN': xsrf,
    },
    body: JSON.stringify({ company_id: companyId }),
  })
  if (!r.ok) {
    let message = `Wybór spółki nie powiódł się (${r.status})`
    try {
      const j = (await r.json()) as { message?: string; error?: string }
      message = j.message ?? j.error ?? message
    } catch {
      // not JSON
    }
    throw new ApiError(r.status, message)
  }
  return (await r.json()) as SelectCompanyResponse
}

type CompanyPickerResolver = () => Promise<void>

let companyPickerResolver: CompanyPickerResolver | null = null

export function registerCompanyPicker(resolver: CompanyPickerResolver | null): void {
  companyPickerResolver = resolver
}

type ToastEmitter = (message: string) => void

let toastEmitter: ToastEmitter | null = null

export function registerErrorToast(emitter: ToastEmitter | null): void {
  toastEmitter = emitter
}

export interface ApiFetchOptions extends RequestInit {
  companyId?: string
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
  retried = false,
): Promise<T> {
  const { companyId, headers, ...rest } = options
  const xsrf = readXsrfToken()
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-XSRF-TOKEN': xsrf,
    ...(headers as Record<string, string> | undefined),
  }
  if (companyId) finalHeaders['X-Company-Id'] = companyId

  if (import.meta.env.DEV) {
    console.log('[apiFetch→]', rest.method ?? 'GET', path, { xsrfPresent: !!xsrf, xsrfLen: xsrf.length, companyId, body: rest.body ? String(rest.body).slice(0, 200) : null })
  }

  let r: Response
  try {
    r = await fetch(path, {
      credentials: 'include',
      ...rest,
      headers: finalHeaders,
    })
  } catch (networkErr) {
    if (import.meta.env.DEV) {
      console.error('[apiFetch×NETWORK]', rest.method ?? 'GET', path, networkErr)
    }
    throw new ApiError(0, `Network error: ${networkErr instanceof Error ? networkErr.message : String(networkErr)}`)
  }
  if (!r.ok) {
    let message = `Request failed (${r.status})`
    let errorCode: string | undefined
    let errors: Record<string, string[]> | undefined
    let payload: Record<string, unknown> | undefined
    let rawBody = ''
    try {
      rawBody = await r.text()
      const j = JSON.parse(rawBody) as {
        message?: string
        error?: string
        errors?: Record<string, string[]>
      } & Record<string, unknown>
      errorCode = j.error
      message = j.message ?? j.error ?? message
      errors = j.errors
      payload = j
    } catch {
      // not JSON — keep rawBody for diagnostic
    }

    if (r.status === 428 && errorCode === 'company_picker_required' && !retried && companyPickerResolver) {
      await companyPickerResolver()
      return apiFetch<T>(path, options, true)
    }

    if (r.status === 422 && errorCode === 'company_mismatch' && toastEmitter) {
      toastEmitter(message || 'Nie można powiązać rekordów z różnych spółek')
    }

    if (import.meta.env.DEV) {
      console.error('[apiFetch]', rest.method ?? 'GET', path, '→', r.status, { message, errors, rawBody, xsrfPresent: !!xsrf })
    }
    throw new ApiError(r.status, message, errors, errorCode, payload)
  }
  if (r.status === 204) return undefined as T
  return (await r.json()) as T
}
